import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, inr } from "../../lib/api";
import { ArrowRight, Star, Truck, Leaf, Award, Sparkles, Gift, Coffee } from "lucide-react";
import WishlistHeart from "../../components/store/WishlistHeart";

import FlashSaleBanner from "../../components/store/FlashSaleBanner";
import { useSeo } from "../../lib/useSeo";

const HERO_VIDEO = "/api/media/dailybean/videos/site/571f280ee821457ba987eb57708677fa.mp4";
const HERO_MAIN = "https://images.pexels.com/photos/18280357/pexels-photo-18280357.jpeg?w=1200";
const HERO_ACCENT = "https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg?w=800";
const RITUAL_IMG = "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200";
const LIFESTYLE_IMG = "https://images.pexels.com/photos/7388896/pexels-photo-7388896.jpeg?w=1200";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};
const reveal = {
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, margin: "-80px" },
  variants: fadeUp,
};

export default function Home() {
  const [cats, setCats] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [settings, setSettings] = useState({
    show_categories_bento: true, show_bestsellers: true, show_new_arrivals: true,
    show_ritual: true, show_why_us: true, show_testimonials: true, show_cta: true,
  });

  useSeo({
    title: "DailyBeans — Estate-sourced South Indian coffee, delivered fresh",
    description: "Small-batch coffee roasted weekly from Coorg, Chikmagalur & Nilgiris estates. Free shipping over ₹500. Try code WELCOME10 for 10% off.",
    canonical: "https://mydailybean.com/",
  });

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/products?sort=popularity&limit=4"),
      api.get("/products?sort=newest&limit=4"),
      api.get("/storefront/settings"),
    ]).then(([c, b, n, s]) => {
      setCats(c.data); setBestsellers(b.data); setNewArrivals(n.data); setSettings(s.data);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <FlashSaleBanner />
      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        <div className="blob blob-gold" style={{ width: 500, height: 500, top: -100, right: -100 }} />
        <div className="blob blob-terra" style={{ width: 400, height: 400, bottom: -80, left: -80 }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12 pb-24 grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#D4AF37]/30 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="uppercase text-[10px] tracking-[0.25em] text-[#8B7355] font-semibold">Est. 2024 · Small-batch roasted</span>
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] text-[#1C1917]">
              Your daily dose<br /><span className="italic text-[#8B7355]">of good</span> <span className="relative inline-block">
                coffee.
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none">
                  <path d="M2 8 Q100 -2 198 6" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round" fill="none"/>
                </svg>
              </span>
            </h1>
            <p className="mt-6 text-stone-600 text-lg max-w-md leading-relaxed">
              Freshly roasted beans from Coorg, Chikmagalur & the Nilgiris — plus tea, snacks and gift sets. Brewed with care. Made for you.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-now" className="group bg-[#3B2F2F] text-[#FDFBF7] rounded-full px-6 py-3 font-medium hover:bg-black inline-flex items-center gap-2 shadow-lg shadow-[#3B2F2F]/20 hover:shadow-xl hover:shadow-[#3B2F2F]/30 transition-all">
                Shop Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/shop?sort=popularity" data-testid="hero-bestsellers" className="border-2 border-[#3B2F2F]/20 text-[#3B2F2F] rounded-full px-6 py-3 font-medium hover:bg-white hover:border-[#3B2F2F]/60 transition-colors">
                Best Sellers
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-stone-500">
              <div className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#D4AF37]"/> Free ship ₹500+</div>
              <div className="flex items-center gap-1.5"><Leaf className="w-4 h-4 text-[#2E4A35]"/> Estate direct</div>
              <div className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#993322]"/> Roasted weekly</div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-[#D4AF37]/20 hidden md:block" />
            <div className="rounded-3xl shadow-2xl shadow-[#3B2F2F]/20 w-full h-[520px] overflow-hidden relative z-10 bg-[#3B2F2F]">
              <video
                autoPlay
                loop
                muted
                playsInline
                poster={HERO_MAIN}
                className="w-full h-full object-cover"
                data-testid="hero-video"
              >
                <source src="https://videos.pexels.com/video-files/2098989/2098989-hd_1920_1080_30fps.mp4" type="video/mp4" />
                <source src="https://cdn.pixabay.com/video/2022/10/17/135458-761266779_large.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"/>
            </div>
            <img src={HERO_ACCENT} alt="Latte" className="hidden md:block absolute -bottom-8 -left-8 w-48 h-48 rounded-2xl object-cover shadow-xl shadow-[#3B2F2F]/30 border-4 border-[#FDFBF7] z-20" />
            <div className="absolute -bottom-4 right-4 md:right-16 bg-white/95 backdrop-blur p-4 rounded-2xl shadow-2xl z-30 flex items-center gap-3">
              <div className="flex text-[#D4AF37]">
                {Array.from({length:5}).map((_,i)=><Star key={i} className="w-4 h-4 fill-current"/>)}
              </div>
              <div>
                <div className="text-sm font-outfit font-bold">4.9 / 5</div>
                <div className="text-xs text-stone-500">4,200+ reviews</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo strip */}
      <motion.section {...reveal} className="bg-[#3B2F2F] text-[#FDFBF7] py-6 relative overflow-hidden">
        <div className="pattern-beans absolute inset-0 opacity-20"/>
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 relative">
          {[
            {icon: Coffee, title: "Fresh Roast", sub: "Every week"},
            {icon: Truck, title: "Free Shipping", sub: "Above ₹500"},
            {icon: Leaf, title: "Estate Direct", sub: "From the source"},
            {icon: Gift, title: "Gift Sets", sub: "Ready to send"},
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-[#D4AF37]"/>
              </div>
              <div>
                <div className="font-outfit font-semibold text-sm">{f.title}</div>
                <div className="text-xs text-[#FDFBF7]/60">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Categories */}
      {settings.show_categories_bento && cats.length > 0 && (
      <motion.section {...reveal} className="max-w-7xl mx-auto px-4 md:px-8 py-20 relative">
        <div className="blob blob-sage" style={{ width: 380, height: 380, top: 100, right: -100 }} />
        <div className="flex items-end justify-between mb-10 relative z-10">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-3">The Collection</div>
            <h2 className="font-serif-display text-4xl md:text-6xl">Explore the shelf</h2>
          </div>
          <Link to="/shop" className="text-sm text-[#3B2F2F] hover:text-[#D4AF37] flex items-center gap-1 shrink-0">
            All products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
          {cats.map((c, i) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`cat-tile-${c.slug}`}
                  className={`group relative overflow-hidden rounded-3xl ${i%3===0?"md:row-span-2 md:h-[500px]":"h-56"}`}>
              <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3B2F2F]/90 via-[#3B2F2F]/30 to-transparent" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-[#D4AF37]/40 to-transparent"/>
              <div className="absolute bottom-5 left-5 text-white">
                <div className="font-serif-display text-2xl md:text-3xl">{c.name}</div>
                <div className="text-xs text-white/80 mt-1 flex items-center gap-1 group-hover:text-[#D4AF37] transition-colors">
                  Shop now <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </motion.section>
      )}

      {/* Bestsellers on cream section */}
      {settings.show_bestsellers && bestsellers.length > 0 && (
      <motion.section {...reveal} className="section-cream py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-3">Top of the ranks</div>
              <h2 className="font-serif-display text-4xl md:text-6xl">Best sellers this month</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {bestsellers.map(p => <ProductCard key={p.product_id} p={p} />)}
          </div>
        </div>
      </motion.section>
      )}

      {/* Ritual — terracotta section */}
      {settings.show_ritual && (
      <motion.section {...reveal} className="section-terracotta relative overflow-hidden py-24">
        <div className="pattern-beans absolute inset-0 opacity-30"/>
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-12 items-center relative">
          <div className="relative">
            <img src={RITUAL_IMG} alt="" className="rounded-3xl h-[440px] w-full object-cover shadow-2xl shadow-[#3B2F2F]/30"/>
            <div className="absolute -top-4 -left-4 bg-white rounded-2xl px-4 py-3 shadow-xl hidden md:block">
              <div className="font-outfit font-bold text-[#3B2F2F]">Save 15%</div>
              <div className="text-xs text-stone-500">On every recurring order</div>
            </div>
          </div>
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#3B2F2F] mb-3">The Weekend Ritual</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#3B2F2F]">Subscribe.<br/>Save 15%.<br/>Never run out.</h2>
            <p className="text-[#3B2F2F]/80 mt-6 max-w-md leading-relaxed">Fresh beans on your doorstep every week or month. Pause anytime. Cancel anytime. Because your morning shouldn&rsquo;t depend on grocery runs.</p>
            <Link to="/shop/gift-boxes" className="inline-flex items-center gap-2 bg-[#3B2F2F] text-[#FDFBF7] rounded-full px-6 py-3 mt-8 hover:bg-black transition-colors">
              Explore subscriptions <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </motion.section>
      )}

      {/* New arrivals — sage section */}
      {settings.show_new_arrivals && newArrivals.length > 0 && (
      <motion.section {...reveal} className="section-sage py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="uppercase text-xs tracking-[0.3em] text-[#3B2F2F]/70 mb-3">Just landed</div>
              <h2 className="font-serif-display text-4xl md:text-6xl text-[#3B2F2F]">New arrivals</h2>
            </div>
            <Link to="/shop?sort=newest" className="text-sm text-[#3B2F2F] hover:text-[#D4AF37] flex items-center gap-1">
              See all <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {newArrivals.map(p => <ProductCard key={p.product_id} p={p} />)}
          </div>
        </div>
      </motion.section>
      )}

      {/* Why us */}
      {settings.show_why_us && (
      <motion.section {...reveal} className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="text-center mb-12">
          <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-3">The DailyBeans promise</div>
          <h2 className="font-serif-display text-4xl md:text-6xl">Why coffee lovers choose us</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: Leaf, title: "Estate direct", body: "We source directly from small growers across Coorg, Chikmagalur & the Nilgiris.", color:"#2E4A35", bg:"#E8EDDF"},
            {icon: Award, title: "Roasted weekly", body: "Beans roasted in tiny batches every week — never sitting on shelves growing stale.", color:"#993322", bg:"#F5DEC7"},
            {icon: Truck, title: "Free shipping ₹500+", body: "Nationwide 2-4 day delivery. Free on orders above ₹500. Tracked end-to-end.", color:"#D4AF37", bg:"#FEF3E2"},
          ].map((f,i)=>(
            <div key={i} className="relative bg-white rounded-3xl p-8 border border-stone-100 shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-40" style={{background: f.bg}}/>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center relative" style={{background: f.bg}}>
                <f.icon className="w-6 h-6" style={{color: f.color}}/>
              </div>
              <h3 className="font-serif-display text-3xl mt-5 relative">{f.title}</h3>
              <p className="text-stone-600 mt-3 leading-relaxed relative">{f.body}</p>
            </div>
          ))}
        </div>
      </motion.section>
      )}

      {/* Testimonials */}
      {settings.show_testimonials && (
      <motion.section {...reveal} className="section-espresso text-[#FDFBF7] py-24 relative overflow-hidden">
        <div className="blob blob-gold" style={{ width: 500, height: 500, top: -100, left: -100, opacity: 0.15 }} />
        <div className="blob blob-terra" style={{ width: 400, height: 400, bottom: -100, right: -100, opacity: 0.2 }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-14">
            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-3">Kind words</div>
            <h2 className="font-serif-display text-4xl md:text-6xl">From your kitchen counters</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {n:"Aditi R.", loc:"Bengaluru", c:"The Malabar Monsoon is genuinely the best cup I've made at home. Ships fast, arrives fresh, every single time.", img: LIFESTYLE_IMG},
              {n:"Rohan P.", loc:"Mumbai", c:"Switched to their filter decoction — my mornings have never been the same. Bold, smooth, and always consistent.", img: "https://images.pexels.com/photos/4921029/pexels-photo-4921029.jpeg?w=400"},
              {n:"Meera K.", loc:"Delhi", c:"The gift box is stunning. Ordered three for the festive season, everyone loved it. Beautiful packaging, better coffee.", img: "https://images.pexels.com/photos/7388896/pexels-photo-7388896.jpeg?w=400"},
            ].map((t,i)=>(
              <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-7 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex text-[#D4AF37] mb-4">{Array.from({length:5}).map((_,j)=><Star key={j} className="w-4 h-4 fill-current"/>)}</div>
                <p className="font-serif-display text-2xl leading-tight">&ldquo;{t.c}&rdquo;</p>
                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
                  <img src={t.img} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-[#D4AF37]/30"/>
                  <div>
                    <div className="font-outfit font-semibold text-sm">{t.n}</div>
                    <div className="text-xs text-[#FDFBF7]/50">{t.loc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>
      )}

      {/* CTA */}
      {settings.show_cta && (
      <motion.section {...reveal} className="section-warm py-24 relative overflow-hidden">
        <div className="pattern-beans absolute inset-0 opacity-40"/>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative">
          <h2 className="font-serif-display text-5xl md:text-7xl text-[#3B2F2F]">Ready for a<br/>better morning?</h2>
          <p className="text-stone-600 mt-6 text-lg max-w-lg mx-auto">Start with our best-selling Malabar Monsoon Arabica. Use code <code className="bg-[#3B2F2F] text-[#FDFBF7] px-2 py-0.5 rounded font-outfit text-base">WELCOME10</code> for 10% off your first order.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#3B2F2F] text-[#FDFBF7] rounded-full px-8 py-4 mt-10 text-lg hover:bg-black transition-colors shadow-2xl shadow-[#3B2F2F]/30">
            Start shopping <ArrowRight className="w-5 h-5"/>
          </Link>
        </div>
      </motion.section>
      )}
    </div>
  );
}

export function ProductCard({ p }) {
  return (
    <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`}
          className="product-card group block bg-white rounded-3xl overflow-hidden border border-stone-100 hover:border-[#D4AF37]/40 relative">
      <WishlistHeart productId={p.product_id} variant="card"/>
      <div className="aspect-square overflow-hidden bg-gradient-to-br from-[#F5F0E6] to-[#FEF3E2] relative">
        <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
        {p.is_bestseller && <span className="absolute top-3 left-3 bg-[#D4AF37] text-[#3B2F2F] text-[10px] font-outfit font-bold uppercase tracking-widest px-2 py-1 rounded-full">Best Seller</span>}
        {p.is_new && !p.is_bestseller && <span className="absolute top-3 left-3 bg-[#2E4A35] text-white text-[10px] font-outfit font-bold uppercase tracking-widest px-2 py-1 rounded-full">New</span>}
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B7355]">{p.category_slug?.replace(/-/g," ")}</div>
        <div className="font-serif-display text-xl mt-1 leading-tight min-h-[3rem]">{p.name}</div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
          <div>
            <div className="text-[10px] text-stone-400 uppercase tracking-widest">From</div>
            <div className="font-outfit text-lg font-bold text-[#3B2F2F]">{inr(p.min_price)}</div>
          </div>
          <div className="flex items-center gap-1 text-xs bg-[#F5F0E6] px-2 py-1 rounded-full">
            <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]"/>
            <span className="font-outfit font-semibold">{p.rating}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
